import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, message, Empty, Typography, Popconfirm, Space, Tag } from 'antd';
import AppLoading from '../components/AppLoading';
import { UndoOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { fetchTrashScenes, restoreScene, permanentDeleteScene } from '../api/scenes';

const { Title, Text } = Typography;

interface TrashItem {
  id: string;
  name: string;
  description?: string;
  deletedAt: string;
  updatedAt: string;
}

function getDaysRemaining(deletedAt: string): number {
  const deleted = new Date(deletedAt);
  const expiry = new Date(deleted);
  expiry.setDate(expiry.getDate() + 30);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function RecycleBin() {
  const navigate = useNavigate();
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrashScenes()
      .then((data) => setItems(data))
      .catch(() => message.error('加载回收站失败'))
      .finally(() => setLoading(false));
  }, []);

  const handleRestore = async (id: string) => {
    try {
      await restoreScene(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      message.success('项目已恢复');
    } catch {
      message.error('恢复失败');
    }
  };

  const handlePermanentDelete = async (id: string) => {
    try {
      await permanentDeleteScene(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      message.success('项目已永久删除');
    } catch {
      message.error('删除失败');
    }
  };

  if (loading) {
    return <AppLoading />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/projects')}>
            返回
          </Button>
          <Title level={3} style={{ margin: 0 }}>回收站</Title>
        </Space>
        <Text type="secondary">删除的项目保留30天后自动清理</Text>
      </div>

      {items.length === 0 ? (
        <Empty
          description="回收站为空"
          style={{ marginTop: 80 }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {items.map((item) => {
            const days = getDaysRemaining(item.deletedAt);
            return (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  actions={[
                    <Popconfirm
                      title="确定恢复该项目？"
                      description="恢复后将回到项目列表"
                      onConfirm={() => handleRestore(item.id)}
                      okText="恢复"
                      cancelText="取消"
                    >
                      <Button type="link" icon={<UndoOutlined />}>
                        恢复
                      </Button>
                    </Popconfirm>,
                    <Popconfirm
                      title="确定永久删除？"
                      description="此操作不可恢复"
                      onConfirm={() => handlePermanentDelete(item.id)}
                      okText="永久删除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                    >
                      <Button type="link" danger icon={<DeleteOutlined />}>
                        永久删除
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <Card.Meta
                    title={item.name}
                    description={
                      <div>
                        <p style={{ margin: 0, color: '#666', fontSize: 13 }}>{item.description || '暂无描述'}</p>
                        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#999' }}>
                          删除于：{new Date(item.deletedAt).toLocaleDateString('zh-CN')}
                        </p>
                        <Tag color={days <= 3 ? 'red' : days <= 7 ? 'orange' : 'green'} style={{ marginTop: 8 }}>
                          {days > 0 ? `剩余 ${days} 天` : '即将删除'}
                        </Tag>
                      </div>
                    }
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}
